import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { EyeIcon, EyeSlashIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

const Input = React.forwardRef(
  (
    {
      id,
      name,
      type = 'text',
      placeholder = '',
      maxLength = undefined,
      disabled = false,
      readOnly = false,
      required = false,
      autoComplete,
      className = '',
      fullWidth = true,

      label,
      label_description,
      info,
      note,
      error,

      leftIcon,
      rightIcon,

      inputGroupLeft,
      inputGroupRight,

      as = 'input',
      options = [],

      onChange,
      onBlur,
      value,
      checked,
      ...rest
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const comboboxRef = useRef(null);
    const isPassword = type === 'password';
    const finalType = isPassword && showPassword ? 'text' : type;

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    const baseInputClasses =
      'block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none text-base bg-white dark:bg-black text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-900';

    const RadioClasses = clsx(
      'rounded border-gray-300 dark:border-gray-700 text-emerald-600 dark:text-emerald-400',
      disabled && 'cursor-not-allowed opacity-60',
      readOnly && 'cursor-default opacity-60',
      error && 'border-red-500'
    );

    const checkboxClasses = clsx(
      'col-start-1 row-start-1 appearance-none rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-black checked:border-indigo-600 dark:checked:border-indigo-400 checked:bg-indigo-600 dark:checked:bg-indigo-400 indeterminate:border-indigo-600 dark:indeterminate:border-indigo-400 indeterminate:bg-indigo-600 dark:indeterminate:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-400 disabled:border-gray-300 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:checked:bg-gray-100 dark:disabled:checked:bg-gray-900 forced-colors:appearance-auto',
      disabled && 'cursor-not-allowed opacity-60',
      readOnly && 'cursor-default opacity-60',
      error && 'border-red-500 focus:ring-red-500'
    );

    // Handle clicks outside the combobox
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
          setShowOptions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Filter options based on input value
    useEffect(() => {
      if (as === 'combobox') {
        setFilteredOptions(
          options.filter((option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
          )
        );
      }
    }, [inputValue, options, as]);

    // Set initial value for combobox
    useEffect(() => {
      if (as === 'combobox') {
        // Only proceed if we have both value and options
        if (value && options.length > 0) {
          const stringValue = String(value);
          const selectedOption = options.find(opt => String(opt.value) === stringValue);
          
          if (selectedOption) {
            setInputValue(selectedOption.label);
          } else {
            setInputValue(stringValue); // Fallback to show ID if no match
            console.warn('No matching option found for value:', value, 'Options:', options);
          }
        } else if (!value) {
          setInputValue(''); // Clear if no value
        }
      }
    }, [value, options, as]);

    const handleComboboxSelect = (option) => {
      setInputValue(option.label); // Show the name
      onChange?.({ target: { name, value: option.value } }); // Store the ID
      setShowOptions(false);
    };

    const handleComboboxInputChange = (e) => {
      setInputValue(e.target.value);
      if (!e.target.value) {
        onChange?.({ target: { name, value: '' } });
      }
    };

    const clearSelection = () => {
      setInputValue('');
      onChange?.({ target: { name, value: '' } });
    };

    if (as === 'radio') {
      return (
        <div className={clsx('flex items-center gap-2', fullWidth && 'w-full')}>
          <input
            id={id || name}
            name={name}
            type={as}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            className={RadioClasses}
            ref={ref}
            onChange={onChange}
            onBlur={onBlur}
            checked={checked}
            {...rest}
          />
          {label && (
            <label
              htmlFor={id || name}
              className={clsx(
                'select-none text-sm font-medium text-gray-700 dark:text-gray-300',
                disabled && 'cursor-not-allowed',
                readOnly && 'cursor-default'
              )}
            >
              {label}
            </label>
          )}
          {info && !error && (
            <p className="ml-7 text-xs text-gray-600 dark:text-gray-400 dark:text-gray-400">{info}</p>
          )}
          {note && !error && (
            <p className="ml-7 text-xs text-emerald-600 dark:text-emerald-400">{note}</p>
          )}
          {error && (
            <p className="ml-7 text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      );
    }
    else if (as === 'checkbox') {
      return (
        <div className={clsx('flex items-center gap-2', fullWidth && 'w-full')}>
          <div className="flex gap-3">
            <div className="flex h-6 shrink-0 items-center">
              <div className="group grid size-4 grid-cols-1">
                <input
                  id={id || name}
                  name={name}
                  type={as}
                  value={value}
                  disabled={disabled}
                  readOnly={readOnly}
                  required={required}
                  className={checkboxClasses}
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  checked={checked}
                  {...rest}
                />
                <svg
                  fill="none"
                  viewBox="0 0 14 14"
                  className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                >
                  <path
                    d="M3 8L6 11L11 3.5"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-0 group-has-[:checked]:opacity-100"
                  />
                  <path
                    d="M3 7H11"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-0 group-has-[:indeterminate]:opacity-100"
                  />
                </svg>
              </div>
            </div>
            <div className="text-sm/6">
              {label && (
                <>
                  <label
                    htmlFor={id || name}
                    className={clsx(
                      'select-none text-sm font-medium text-gray-700 dark:text-gray-300',
                      disabled && 'cursor-not-allowed',
                      readOnly && 'cursor-default'
                    )}
                  >
                    {label}
                  </label>
                  {label_description && (
                    <p id="comments-description" className="text-gray-500">
                      {label_description}
                    </p>
                  )}
                </>
              )}
              {info && !error && (
                <p className="ml-7 text-xs text-gray-600 dark:text-gray-400 dark:text-gray-400">{info}</p>
              )}
              {note && !error && (
                <p className="ml-7 text-xs text-emerald-600 dark:text-emerald-400">{note}</p>
              )}
              {error && (
                <p className="ml-7 text-xs text-red-600 dark:text-red-400">{error}</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    const inputClassNames = clsx(
      baseInputClasses,
      error
        ? 'border-red-500 text-red-900 dark:text-red-100 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 dark:border-gray-700 focus:border-emerald-500 focus:ring-emerald-500',
      disabled && 'bg-gray-100 dark:bg-gray-900 cursor-not-allowed',
      readOnly && 'bg-gray-100 dark:bg-gray-900',
      className
    );

    const sharedProps = {
      id: id || name,
      name,
      disabled,
      readOnly,
      required,
      placeholder,
      maxLength,
      className: clsx(
        inputClassNames,
        leftIcon && 'pl-10',
        (rightIcon || isPassword) && 'pr-10',
        inputGroupLeft && 'rounded-l-none',
        inputGroupRight && 'rounded-r-none'
      ),
      onChange: as === 'combobox' ? handleComboboxInputChange : onChange,
      onBlur,
      value: as === 'combobox' ? inputValue : value,
      ref,
      autoComplete,
      ...rest,
    };

    return (
      <div className={clsx('flex flex-col gap-1', fullWidth && 'w-full')}>
        <label
          htmlFor={id || name}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required === true ? ' *' : <span className="text-xs text-gray-600 dark:text-gray-400"> (optional)</span>}
        </label>

        <div className="relative flex items-stretch w-full">
          {inputGroupLeft && (
            <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 text-sm text-gray-600 dark:text-gray-400">
              {inputGroupLeft}
            </span>
          )}

          <div className="relative flex-grow">
            {leftIcon && (
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600 dark:text-gray-400">
                {leftIcon}
              </div>
            )}

            {as === 'textarea' ? (
              <textarea {...sharedProps} rows={rest.rows || 3} />
            ) : as === 'select' ? (
              <>
                <select {...sharedProps} autoComplete="off">
                  <option value="" disabled>
                    {placeholder || 'Select an option'}
                  </option>
                  {options.map(({ value: optValue, label: optLabel }) => (
                    <option key={optValue} value={optValue}>
                      {optLabel}
                    </option>
                  ))}
                </select>
              </>
            ) : as === 'combobox' ? (
              <div ref={comboboxRef} className="relative">
                <input
                  {...sharedProps}
                  autoComplete="off"
                  type="text"
                  value={inputValue || (options.length === 0 ? 'Loading...' : '')}
                  onClick={() => setShowOptions(true)}
                  aria-autocomplete="list"
                  aria-expanded={showOptions}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  {inputValue && (
                    <button
                      type="button"
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-500 mr-1"
                      onClick={clearSelection}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-500"
                    onClick={() => setShowOptions(!showOptions)}
                  >
                    <ChevronUpDownIcon className="h-5 w-5" />
                  </button>
                </div>
                {showOptions && (
                  <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-black py-1 text-base shadow-lg ring-1 ring-black dark:ring-white ring-opacity-5 focus:outline-none">
                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option) => (
                        <li
                          key={option.value}
                          className="relative cursor-default select-none py-2 pl-3 pr-9 hover:bg-emerald-100 dark:hover:bg-emerald-900 text-black dark:text-white"
                          onClick={() => handleComboboxSelect(option)}
                        >
                          <span className="block truncate">{option.label}</span>
                          {value === option.value && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-emerald-600 dark:text-emerald-400">
                              <svg
                                className="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-600 dark:text-gray-400">
                        No options found
                      </li>
                    )}
                  </ul>
                )}
              </div>
            ) : (
              <input {...sharedProps} type={finalType} />
            )}

            {/* Password toggle or custom right icon */}
            {isPassword ? (
              <button
                type="button"
                tabIndex={-1}
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 dark:text-gray-400 hover:text-gray-500"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            ) : (
              rightIcon && as !== 'combobox' && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 dark:text-gray-400">
                  {rightIcon}
                </div>
              )
            )}
          </div>

          {inputGroupRight && (
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-100 px-3 text-sm text-gray-600 dark:text-gray-400">
              {inputGroupRight}
            </span>
          )}
        </div>

        {info && !error && (
          <p className="text-xs text-gray-600 dark:text-gray-400">{info}</p>
        )}
        {note && !error && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400">{note}</p>
        )}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;